import { ShiftBackups } from '../api/types';

import useAuth from '@/hooks/useAuth';

type Props = {
  shiftId: number;
  backups?: ShiftBackups | null;
  /** The wrapper's class: the Week card's row or the Day card's group. */
  className: string;
};

const OFF_TITLE: Record<'job' | 'order', string> = {
  job: 'Auto backups are off for this job.',
  order: 'Auto backups are off for this order.',
};

const countsText = ({ on_standby, invited }: ShiftBackups, short: boolean) => {
  const parts: string[] = [];
  if (on_standby > 0) parts.push(`${on_standby} on standby`);
  if (invited > 0)
    parts.push(
      short || on_standby > 0
        ? `${invited} invited`
        : `${invited} ${invited === 1 ? 'backup' : 'backups'} invited`,
    );
  return parts.join(' · ');
};

/**
 * The main chip's text: "Auto backups" (with any counts) on a shift where
 * they run, else just the counts of a shift that still has backups after
 * being turned off. Empty when there is nothing to say.
 */
export const backupsChipText = (backups: ShiftBackups) =>
  backups.enabled === true
    ? ['Auto backups', countsText(backups, true)].filter(Boolean).join(' · ')
    : countsText(backups, false);

/**
 * Auto backups on a calendar shift card, Week and Day (GRAVY-10898):
 *
 * - green "Auto backups" (plus how many are on standby / invited) where they
 *   run for the shift — a missing `enabled` is read as off;
 * - the counts alone on a shift that has backups but is now off;
 * - muted "Backups off" only when a JOB or ORDER was switched off under a
 *   client that has them on. Never for an off client: clients are opt-in, so
 *   most are off and a chip on every one of their cards would be noise.
 *
 * Tenant admins only, like every backups control. `backups` is absent from an
 * API older than the per-level setting, and then nothing renders.
 */
const BackupsChips = ({ shiftId, backups, className }: Props) => {
  const { currentAdminIsTenantAdmin } = useAuth();
  if (!currentAdminIsTenantAdmin || !backups) return null;

  const text = backupsChipText(backups);
  const on = backups.enabled === true;
  const offLevel =
    backups.off_level === 'job' || backups.off_level === 'order'
      ? backups.off_level
      : null;
  if (!text && !offLevel) return null;

  return (
    <span className={className}>
      {text && (
        <span
          className={`dashv2-backups-chip${on ? ' is-on' : ''}`}
          data-testid={`card-backups-${shiftId}`}
          data-enabled={on ? 'true' : 'false'}
        >
          {text}
        </span>
      )}
      {offLevel && (
        <span
          className="dashv2-backups-chip is-off"
          data-testid={`card-backups-off-${shiftId}`}
          data-level={offLevel}
          title={OFF_TITLE[offLevel]}
        >
          Backups off
        </span>
      )}
    </span>
  );
};

export default BackupsChips;
